import {_RequestEnvelope} from '../core/types';
import {
    PriceQuoteSettings,
    SkinDeckCallbackInfo,
    SkinDeckSettings,
    SkinPurchaseDetails,
    SkinPurchaseSettings,
    SkinPurchaseStatusType,
    SkinPurchaseSummary,
    SteamSettings,
} from '../models/steam';

// ---------------------------------------------------------------------------
// CheckSkinPurchase
// ---------------------------------------------------------------------------

export interface CheckSkinPurchaseRequest {
    PurchaseId: string;
    RealmId: string | null;
}

export const CheckSkinPurchaseResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
} as const;
export type CheckSkinPurchaseResult =
    typeof CheckSkinPurchaseResultCode[keyof typeof CheckSkinPurchaseResultCode];

export type CheckSkinPurchaseResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Purchase: SkinPurchaseDetails;
}
    | {
    Result: 'Unknown' | 'NotFound';
    ResultDescription: string;
    Purchase: null;
};

const _checkSkinPurchaseMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
};

export function checkSkinPurchase(
    params: CheckSkinPurchaseRequest
): _RequestEnvelope<CheckSkinPurchaseResponse> {
    return {...params, _endpoint: 'admin/steam/checkSkinPurchase', _resultCodeMap: _checkSkinPurchaseMap};
}

// ---------------------------------------------------------------------------
// GetSettings
// ---------------------------------------------------------------------------

export interface GetSettingsResponse {
    Settings: SteamSettings;
}

export function getSettings(): _RequestEnvelope<GetSettingsResponse> {
    return {_endpoint: 'admin/steam/getSettings', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// GetSkinPurchase
// ---------------------------------------------------------------------------

export interface GetSkinPurchaseRequest {
    PurchaseId: string;
    RealmId: string | null;
}

export const GetSkinPurchaseResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
} as const;
export type GetSkinPurchaseResult =
    typeof GetSkinPurchaseResultCode[keyof typeof GetSkinPurchaseResultCode];

export type GetSkinPurchaseResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Purchase: SkinPurchaseDetails;
}
    | {
    Result: 'Unknown' | 'NotFound';
    ResultDescription: string;
    Purchase: null;
};

const _getSkinPurchaseMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
};

export function getSkinPurchase(
    params: GetSkinPurchaseRequest
): _RequestEnvelope<GetSkinPurchaseResponse> {
    return {...params, _endpoint: 'admin/steam/getSkinPurchase', _resultCodeMap: _getSkinPurchaseMap};
}

// ---------------------------------------------------------------------------
// ListSkinDeckCallbacks
// ---------------------------------------------------------------------------

export interface ListSkinDeckCallbacksRequest {
    ExternalId: string | null;
    Limit: number;
}

export interface ListSkinDeckCallbacksResponse {
    Callbacks: SkinDeckCallbackInfo[];
}

export function listSkinDeckCallbacks(params: ListSkinDeckCallbacksRequest): _RequestEnvelope<ListSkinDeckCallbacksResponse> {
    return {...params, _endpoint: 'admin/steam/listSkinDeckCallbacks', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// ListSkinPurchases
// ---------------------------------------------------------------------------

export interface ListSkinPurchasesRequest {
    RealmId: string | null;
    UserId: string | null;
    States: SkinPurchaseStatusType[] | null;
    CreatedFrom: string | null;
    CreatedTo: string | null;
    Skip: number | null;
    Limit: number | null;
}

export interface ListSkinPurchasesResponse {
    Purchases: SkinPurchaseSummary[];
    TotalCount: number;
}

export function listSkinPurchases(params: ListSkinPurchasesRequest): _RequestEnvelope<ListSkinPurchasesResponse> {
    return {...params, _endpoint: 'admin/steam/listSkinPurchases', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// UpdateSettings
// ---------------------------------------------------------------------------

export interface UpdateSettingsRequest {
    SkinDeck: SkinDeckSettings | null;
    Purchase: SkinPurchaseSettings | null;
    PriceQuote: PriceQuoteSettings | null;
}

export const UpdateSettingsResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    InvalidConfig: 'InvalidConfig',
} as const;
export type UpdateSettingsResult =
    typeof UpdateSettingsResultCode[keyof typeof UpdateSettingsResultCode];

export type UpdateSettingsResponse =
    | {
    Result: 'Ok' | 'InvalidConfig';
    ResultDescription: string;
    Settings: SteamSettings;
    ConfigErrors: string[];
}
    | {
    Result: 'Unknown';
    ResultDescription: string;
    Settings: null;
    ConfigErrors: null;
};

const _updateSettingsMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'InvalidConfig',
};

export function updateSettings(
    params: UpdateSettingsRequest
): _RequestEnvelope<UpdateSettingsResponse> {
    return {...params, _endpoint: 'admin/steam/updateSettings', _resultCodeMap: _updateSettingsMap};
}
